import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { TermoCompromissoPublicidadePjService } from '@/core/api/sicom-api/registro/prestador/relatorios/termo-compromisso-publicidade-pj/termo-compromisso-publicidade-pj.service';
import { ReportTermoCompromissoPublicidadePjRequest, ReportTermoCompromissoPublicidadePjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/termo-compromisso-publicidade-pj/termo-compromisso-publicidade-pj.model';
import { TermoCompromissoPublicidadePjJobService } from '@/core/services/termo-compromisso-publicidade-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportTermoCompromissoPublicidadePjRequest;
type Resp = ReportTermoCompromissoPublicidadePjResponse;

/**
 * Relatório PDF assíncrono: Termo de Compromisso para Publicidade PJ.
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (nuCrm/sgUf via `PessoaService`). Sem filtros de assinante/observação,
 * conforme spec `termo-compromisso-publicidade.md`.
 */
@Component({
    selector: 'app-termo-compromisso-publicidade-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, RelatorioContainer, CanDirective],
    templateUrl: './termo-compromisso-publicidade-pj.html',
    styleUrl: './termo-compromisso-publicidade-pj.scss'
})
export class ReportTermoCompromissoPublicidadePjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly termoService = inject(TermoCompromissoPublicidadePjService);
    private readonly pessoaService = inject(PessoaService);
    readonly termoJobService = inject(TermoCompromissoPublicidadePjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.termoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.termoJobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar o termo.'
            });
            return null;
        }

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.termoService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.limparJob();
    }
}
